/**
 * 記録（監査ログ・pages/AuditPage.tsx）の 1 行を人が読む一文にする表（2026-10-05 console/followups）。
 * サーバーが audit_log に書く action のうち、プロジェクトの記録（GET /v1/projects/:projectId/audit = project_id が入る行）に出るものを全部持つ。
 *
 * WHY 表にしたか: 以前は AuditPage.tsx の switch に 15 種類だけあり、ほかの種類（公開の取り消し deployment.cancel・Functions・
 *   独自ドメイン・名前の変更など 50 種類ほど）は `functions.secret.put` のような中の名前がそのまま画面に出ていた。
 *   auditWords.test.ts がサーバーのコードを文字として読み、書かれる種類が全部ここにあるか（無ければ「プロジェクトの記録に出ない種類」と
 *   理由つきで分けてあるか）を確かめる = 新しい種類を足したときの付け忘れで止まる。
 * WHY 別ファイルか: node --test（型を剥がすだけ）で確かめられるように、i18n（React を読む）を import しない。
 *   ここはキーと差し込む値だけを返し、訳すのは AuditPage.tsx（translate）。
 * 訳が要る値（キーの種類の名前）・金額の書式（返金）は i18n / money.ts が要るので AuditPage.tsx に残す（key.create / payments.refund）。
 */

export interface AuditLine {
  /** 辞書のキー（i18n/en.ts の audit.*） */
  key: string;
  vars?: Record<string, string>;
}

type Detail = Record<string, unknown>;

const str = (v: unknown, fallback = '?') => (v === undefined || v === null || v === '' ? fallback : String(v));
/** 利用者の ID は先頭 8 文字だけ（以前の BAN の行と同じ。全部出しても読めない） */
const playerOf = (d: Detail) => ({ id: str(d.playerId, '').slice(0, 8) });
const version = (d: Detail) => ({ version: str(d.version) });
const host = (d: Detail) => ({ host: str(d.hostname) });
/** ストアの名前（URL の :store = apple / google）。固有名詞なので訳さない */
const storeName = (s: unknown) => (s === 'apple' ? 'App Store' : s === 'google' ? 'Google Play' : str(s));

/** detail から一文を作る（detail を見ない種類は vars 無し） */
const TABLE: Record<string, (d: Detail) => AuditLine> = {
  'project.create': (d) => ({ key: 'audit.projectCreate', vars: { slug: str(d.slug, '') } }),
  'project.delete': () => ({ key: 'audit.projectDelete' }),
  'project.rename': (d) => ({ key: 'audit.projectRename', vars: { name: str(d.to, '') } }),
  'project.slug_change': (d) => ({ key: 'audit.slugChange', vars: { from: str(d.from), to: str(d.to) } }),
  // redirect = 古い名前の転送を続けるか（slugRename.ts）。true = 転送を始めた / false = やめた
  'project.slug_redirect': (d) => ({ key: d.redirect ? 'audit.slugRedirectOn' : 'audit.slugRedirectOff', vars: { slug: str(d.slug) } }),
  // プロジェクトの受け渡し（2026-10-21 core/handoff-1a・platform/projectTransfer*.ts）。detail に人のメールは入れていない（招いた相手は伏せ字）
  'project.transfer.invited': (d) => ({ key: 'audit.transferInvited', vars: { email: str(d.toEmail) } }),
  'project.transfer.cancelled': () => ({ key: 'audit.transferCancelled' }),
  'project.transfer.declined': () => ({ key: 'audit.transferDeclined' }),
  'project.transfer.accepted': () => ({ key: 'audit.transferAccepted' }),
  // 共同作業者（2026-10-22 core/handoff-1b・platform/projectMembers.ts）。detail のメールはサーバーが伏せ字にして入れている
  'project.member.invited': (d) => ({ key: 'audit.memberInvited', vars: { email: str(d.email) } }),
  'project.member.removed': (d) => ({ key: 'audit.memberRemoved', vars: { email: str(d.email) } }),
  'project.member.left': () => ({ key: 'audit.memberLeft' }),
  'key.revoke': () => ({ key: 'audit.keyRevoke' }),
  'key.rotate_all': () => ({ key: 'audit.keyRotateAll' }),
  'config.push': (d) => ({ key: 'audit.configPush', vars: version(d) }),
  'auth.provider.update': (d) => ({ key: 'audit.authProviderUpdate', vars: { provider: str(d.provider) } }),
  'auth.provider.delete': (d) => ({ key: 'audit.authProviderDelete', vars: { provider: str(d.provider) } }),

  'player.ban': (d) => (d.reason ? { key: 'audit.playerBanReason', vars: { ...playerOf(d), reason: str(d.reason) } } : { key: 'audit.playerBan', vars: playerOf(d) }),
  'player.unban': (d) => ({ key: 'audit.playerUnban', vars: playerOf(d) }),
  'player.delete': (d) => ({ key: 'audit.playerDelete', vars: playerOf(d) }),
  'player.export': (d) => ({ key: 'audit.playerExport', vars: playerOf(d) }),
  'player.age': (d) => ({ key: 'audit.playerAge', vars: playerOf(d) }),
  'player.sanction': (d) => ({ key: 'audit.playerSanction', vars: playerOf(d) }),
  'player.sanction.revoke': (d) => ({ key: 'audit.playerSanctionRevoke', vars: playerOf(d) }),
  // lift = 期限が来て自動で外れた（actor system・sanctions.ts）
  'player.sanction.lift': (d) => ({ key: 'audit.playerSanctionLift', vars: playerOf(d) }),
  'player.appeal.resolve': (d) => ({ key: d.accepted ? 'audit.appealAccepted' : 'audit.appealDeclined', vars: playerOf(d) }),
  'player.payment_block': (d) => ({ key: 'audit.paymentBlock', vars: playerOf(d) }),
  'player.payment_unblock': (d) => ({ key: 'audit.paymentUnblock', vars: playerOf(d) }),
  'chat.hide': () => ({ key: 'audit.chatHide' }),
  'chat.unhide': () => ({ key: 'audit.chatUnhide' }),

  'deployment.create': (d) => ({ key: 'audit.deploymentCreate', vars: version(d) }),
  'deployment.finalize': (d) => ({ key: 'audit.deploymentFinalize', vars: version(d) }),
  'deployment.activate': (d) => {
    // 前の版より古い版を有効にした = ロールバック。前の版が無い（初回公開）なら通常の公開
    const v = Number(d.version);
    const prev = Number(d.previousVersion);
    return Number.isFinite(prev) && prev > v
      ? { key: 'audit.deploymentRollback', vars: { version: String(v), previous: String(prev) } }
      : { key: 'audit.deploymentActivate', vars: version(d) };
  },
  // アップロード中の下書きを取り消した（hosting/routes.ts cancelDraft・2026-10-04 に足した POST /v1/deployments/:ref/cancel）
  'deployment.cancel': (d) => ({ key: 'audit.deploymentCancel', vars: version(d) }),
  // このプロジェクトの中にもうあるファイル（blender の出力・AI の 3D）を、送り直さずに公開に使えるようにした（hosting/adoptBlobs.ts・2026-10-15 creator/blender-to-publish）。
  // project_id 付きで書く = プロジェクトの記録に出す（公開の前の準備の 1 行・本番の見た目は変えない）。detail は count・bytes だけ
  'blob.adopt': (d) => ({ key: 'audit.blobAdopt', vars: { count: str(d.count) } }),
  // AI の作業場から直接送る受付（2026-10-08 creator/workspace-upload・本体 hosting/uploadSessions.ts。表に足したのは creator/mcp-staging）:
  //   作った（MCP の接続から・まだ何も公開していない）/ 使った（届いた版を公開に回した = detail.version）
  'upload_session.create': () => ({ key: 'audit.uploadSessionCreate' }),
  'upload_session.claim': (d) => ({ key: 'audit.uploadSessionClaim', vars: version(d) }),
  // 運営がサイトの配信を止めた / 戻した（hosting/servingStaff.ts）
  'serving.suspend': () => ({ key: 'audit.servingSuspend' }),
  'serving.resume': () => ({ key: 'audit.servingResume' }),
  // 運営の「昇格」（2026-10-13 compute/hosting-promote-impl・hosting/promote/staffRoutes.ts）= よく遊ばれるゲームの画像・音などを
  //   専用の配信に載せて速く・安く届ける。作者には中の言葉（昇格・Worker・ルート）ではなく「速いファイル配信」と見せる。
  //   project_id を入れて書く = プロジェクトの記録に出るので表に持つ（auditWords.test.ts）。どれも外れてもアプリは今までどおり動く（そう書き添える）。
//   文言は「ゲーム」ではなく「公開中のアプリ」（i18n/wording.test.ts のダッシュボードの言葉の決まり）。
  'hosting.promote': () => ({ key: 'audit.hostingPromote' }),
  // keepWorker: true = 一時（held・あとで戻せる）/ 無し・false = 終わり（専用の配信ごと消した）
  'hosting.demote': (d) => ({ key: d.keepWorker ? 'audit.hostingDemotePause' : 'audit.hostingDemote' }),
  // 最後の手段: 全部のゲームの速い配信をまとめて止めた（このゲームも含む = 作者のせいではない、と分かるように書く）
  'hosting.promote_off_all': () => ({ key: 'audit.hostingPromoteOffAll' }),

  'functions.enable': () => ({ key: 'audit.functionsEnable' }),
  'functions.disable': () => ({ key: 'audit.functionsDisable' }),
  'functions.limits': () => ({ key: 'audit.functionsLimits' }),
  'functions.deploy': (d) => ({ key: 'audit.functionsDeploy', vars: version(d) }),
  'functions.delete': () => ({ key: 'audit.functionsDelete' }),
  // suspend / resume = 運営か残高の停止（serviceLifecycle.ts setSuspended）
  'functions.suspend': () => ({ key: 'audit.functionsSuspend' }),
  'functions.resume': () => ({ key: 'audit.functionsResume' }),
  'functions.secret.put': (d) => ({ key: 'audit.secretPut', vars: { name: str(d.name) } }),
  'functions.secret.delete': (d) => ({ key: 'audit.secretDelete', vars: { name: str(d.name) } }),
  // プロジェクトを消した後の片付け（compute/sweep.ts retire）
  'functions.sweep': () => ({ key: 'audit.functionsSweep' }),

  // 独自ドメイン（compute/domains.ts・domainCheck.ts）。action の頭は purpose（functions / hosting）= どちらも同じ一文
  ...domainLines('functions'),
  ...domainLines('hosting'),

  'share.link.create': () => ({ key: 'audit.shareLinkCreate' }),
  // AI の接続の取り消し（platform/delegations.ts・mcpAuthzReplace.ts）。reason = 'replaced' は、つなぎ直し・追加の許可で同じアプリの前の接続を
  // 取り消した（revoke-old・2026-10-06）。WHY 載せるか: この種類は project_id の無いアカウントの記録で、今のプロジェクトの記録には来ない
  // （auditWords.test.ts の ACCOUNT_ONLY）。アカウントの記録を出すときに中の名前がそのまま出ないよう、先に一文を持っておく（founder 2026-10-06）
  'delegation.revoke': (d) => ({ key: d.reason === 'replaced' ? 'audit.delegationReplaced' : 'audit.delegationRevoke' }),
  'appstore.credentials.put': (d) => ({ key: 'audit.storeCredentialsPut', vars: { store: storeName(d.store) } }),
  'appstore.credentials.delete': (d) => ({ key: 'audit.storeCredentialsDelete', vars: { store: storeName(d.store) } }),
  'appstore.rtdn.put': () => ({ key: 'audit.storeNoticesPut' }),
  'appstore.rtdn.delete': () => ({ key: 'audit.storeNoticesDelete' }),
  'payments.connect.create': () => ({ key: 'audit.paymentsConnect' }),

  'liveops.gift.create': () => ({ key: 'audit.giftCreate' }),
  'liveops.gift.cancel': () => ({ key: 'audit.giftCancel' }),
  // codes = 作った数（liveops/routes.ts）
  'liveops.serial.create': (d) => ({ key: 'audit.serialCreate', vars: { count: str(d.codes) } }),
  'liveops.serial.disable': () => ({ key: 'audit.serialDisable' }),
  'mail.sender.update': () => ({ key: 'audit.mailSender' }),
  'mail.broadcast.create': () => ({ key: 'audit.mailCreate' }),
  'mail.broadcast.send': (d) => ({ key: 'audit.mailSend', vars: { count: str(d.recipients, '0') } }),
  'mail.broadcast.cancel': () => ({ key: 'audit.mailCancel' }),

  // 運営の操作（staff）。プロジェクトの記録にも出る = 作者が「何が起きたか」を見られる
  'voice.suspend': (d) => ({ key: 'audit.voiceSuspend', vars: { room: str(d.channel) } }),
  'voice.resume': (d) => ({ key: 'audit.voiceResume', vars: { room: str(d.channel) } }),
  'content.purge': () => ({ key: 'audit.contentPurge' }),
  'abuse_report.resolve': () => ({ key: 'audit.reportResolve' }),
  'ads.suspend': () => ({ key: 'audit.adsSuspend' }),
  'ads.resume': () => ({ key: 'audit.adsResume' }),
  'ads.ads_txt.set': () => ({ key: 'audit.adsTxt' }),
  'ads.network_game.link': () => ({ key: 'audit.adNetworkLink' }),
  'ads.network_game.unlink': () => ({ key: 'audit.adNetworkUnlink' }),
  'ads.revenue_import.run': () => ({ key: 'audit.adsImport' })
};

function domainLines(purpose: 'functions' | 'hosting'): Record<string, (d: Detail) => AuditLine> {
  return {
    [`${purpose}.domain.add`]: (d) => ({ key: 'audit.domainAdd', vars: host(d) }),
    [`${purpose}.domain.remove`]: (d) => ({ key: 'audit.domainRemove', vars: host(d) }),
    [`${purpose}.domain.verified`]: (d) => ({ key: 'audit.domainVerified', vars: host(d) }),
    [`${purpose}.domain.deactivated`]: (d) => ({ key: 'audit.domainDeactivated', vars: host(d) }),
    // ownership_moved = ほかのアカウントが持ち主だと証明した（domainCheck.ts）
    [`${purpose}.domain.released`]: (d) => ({ key: 'audit.domainReleased', vars: host(d) })
  };
}

/** action → 一文のキーと値。表に無い（他モジュールが新しく足した等）なら null = 画面は action をそのまま出す */
export function auditLine(action: string, detail: unknown): AuditLine | null {
  const f = Object.hasOwn(TABLE, action) ? TABLE[action] : undefined;
  return f ? f((detail && typeof detail === 'object' ? detail : {}) as Detail) : null;
}

/** 表にある action の一覧（テスト用） */
export const AUDIT_ACTIONS: readonly string[] = Object.keys(TABLE);
